import React, { useCallback } from 'react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import type { Node } from '../../../types'
import Text from '../text'
import { AutomationTarget } from '../../../types'
import styles from './styles.scss'

const cx = classnames.bind(styles)

interface JsonViewerProps extends AutomationTarget {
  data: Node
  emlId?: string
}

interface JsonNodeProps {
  data: Node
  name?: string
  level?: number
}

const isArrayOfObjects = (data: Node[]): boolean =>
  data.length > 0 &&
  data.some(item => typeof item === 'object' && item !== null)

const ArrayNode = ({ data, level = 0 }: { data: Node[]; level?: number }) => {
  return isArrayOfObjects(data) ? (
    <div className={cx('json-array')}>
      {data.map((item: Node, index: number) => (
        <div key={index}>
          <JsonNode data={item} name={`${index}`} level={level + 1} />
        </div>
      ))}
    </div>
  ) : (
    <div className={cx('json-array')}>
      {data.map((item: Node, index: number) => (
        <div key={index}>
          <Text size='tiny' className={cx('json-value-text')} weight='heavy'>
            {JSON.stringify(item)}
          </Text>
        </div>
      ))}
    </div>
  )
}

const JsonViewer: React.FC<JsonViewerProps> = ({
  data,
  elmId = 'json_viewer',
}) => (
  <div className={cx('json-viewer')} data-elm-id={elmId}>
    <JsonNode data={data} level={0} />
  </div>
)

const JsonNode: React.FC<JsonNodeProps> = ({ data, name, level = 0 }) => {
  const renderValue = useCallback(
    (value: Node, keyName: string, level: number) => {
      const isObject = typeof value === 'object' && value !== null
      const isArray = Array.isArray(value)

      if (isObject || isArray) {
        // array of primitives
        if (isArray && !isArrayOfObjects(value as Node[])) {
          return (
            <div className={cx('json-value')} key={keyName}>
              <Text size='tiny' className={cx('json-key')}>
                {toProperCase(keyName)}:
              </Text>
              <Text
                size='tiny'
                className={cx('json-value-text')}
                weight='heavy'
              >
                {_.isEmpty(value) ? (
                  <i>(empty array)</i>
                ) : (
                  JSON.stringify(value)
                )}
              </Text>
            </div>
          )
        }
        // object or array of objects
        return (
          <div className={cx('json-node')} key={keyName}>
            {keyName && (
              <Text size='tiny' weight='medium' className={cx('json-key')}>
                {toProperCase(keyName)}:
              </Text>
            )}
            {/* don't bump level for arrays */}
            <JsonNode data={value} level={level + (isArray ? 0 : 1)} />
          </div>
        )
      }

      return (
        <div className={cx('json-value')} key={keyName}>
          <Text size='tiny' className={cx('json-key')}>
            {toProperCase(keyName)}:
          </Text>

          <Text size='tiny' className={cx('json-value-text')} weight='heavy'>
            {JSON.stringify(value)}
          </Text>
        </div>
      )
    },
    []
  )

  const renderObjectNode = useCallback(
    (data: Record<string, Node>, level: number) => {
      return _.isEmpty(data) ? (
        <div className={cx('json-value')}>
          <Text size='tiny' className={cx('json-value-text')} weight='heavy'>
            <i>(empty object)</i>
          </Text>
        </div>
      ) : (
        <>
          {Object.entries(data).map(([key, value]) =>
            renderValue(value, key, level)
          )}
        </>
      )
    },
    [renderValue]
  )

  const backgroundColorClass = level % 2 === 0 ? 'bg-even' : 'bg-odd'
  const isObject = typeof data === 'object' && data !== null
  const isArray = Array.isArray(data)
  const toProperCase = (str: string) =>
    str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

  if (isArray && isArrayOfObjects(data as Node[])) {
    return <ArrayNode data={data as Node[]} level={level} />
  }

  return (
    <div
      className={cx('json-node-container', backgroundColorClass)}
      data-level={level}
    >
      {name && (
        <Text size='tiny' color='white' className={cx('json-index')}>
          {toProperCase(name)}
        </Text>
      )}
      <>
        {/* eslint-disable-next-line no-nested-ternary */}
        {isObject ? (
          isArray ? (
            <ArrayNode data={data as Node[]} level={level} />
          ) : (
            renderObjectNode(data, level)
          )
        ) : (
          renderValue(data, name || '', level)
        )}
      </>
    </div>
  )
}

export default JsonViewer
